import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import apiClient from '../../services/apiClient';
import { 
  UserCircleIcon, 
  Cog6ToothIcon, 
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  BellIcon,
  Bars3Icon,
  ChatBubbleLeftRightIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import SupportModal from '../SupportModal';

const Header = ({ onMobileMenuToggle, isMobileSidebarOpen, onSidebarToggle, isSidebarCollapsed }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch notification count
  useEffect(() => {
    const fetchNotificationCount = async () => {
      try {
        const response = await apiClient.get('/notifications/unread-count');
        setNotificationCount(response.count || 0);
      } catch (error) {
        console.error('[Header] Error fetching notification count:', error);
        setNotificationCount(0);
      }
    };
    
    fetchNotificationCount();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotificationCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Generate user display ID (first 8 chars of a hash from user info)
  const generateUserID = () => {
    if (!user?.email) return 'USR12345';
    const hash = user.email.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return `USR${Math.abs(hash).toString().substring(0, 5)}`;
  };

  return (
    <header className="bg-white/90 backdrop-blur-lg shadow-md border-b border-gray-200 h-14 z-50 fixed top-0 left-0 right-0">
      <div className="h-full px-4">
        <div className="flex h-full items-center">
          {/* Sidebar toggle button - works on all screen sizes */}
          <button
            onClick={() => {
              // On mobile (< lg), toggle mobile menu overlay
              // On desktop (>= lg), toggle sidebar collapse
              if (window.innerWidth < 1024) {
                onMobileMenuToggle();
              } else {
                onSidebarToggle();
              }
            }}
            className="p-2 mr-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          {/* Left side - Logo and Customer Portal */}
          <div className="flex items-center space-x-3 flex-1">
            <button
              onClick={() => {
                // Check if user is admin or customer and navigate accordingly
                const isAdmin = user?.role === 'ADMIN';
                navigate(isAdmin ? '/dashboard/admin' : '/dashboard/overview');
              }}
              className="hover:opacity-80 transition-opacity duration-200"
            >
              <img 
                src="https://xloud.tech/s3/.Xlogos/Xloud%20Tech%20logo%20high%20Res.svg" 
                alt="Xloud Technologies" 
                className="h-8 w-auto cursor-pointer"
              />
            </button>
            <div className="h-6 w-px bg-gray-300 hidden sm:block"></div>
            <h1 className="text-base sm:text-lg font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent hidden sm:block">
              {user?.role === 'ADMIN' ? 'Admin Portal' : 'Customer Portal'}
            </h1>
          </div>
          
          {/* Right side - Notifications and User dropdown */}
          <div className="flex items-center space-x-3">
            {/* Support Button */}
            <button
              onClick={() => setIsSupportModalOpen(true)}
              className="relative p-2 rounded-lg hover:bg-green-50 transition-all duration-200 group"
              title="Contact Support"
            >
              <ChatBubbleLeftRightIcon className="w-5 h-5 text-gray-500 group-hover:text-green-600 transition-colors duration-200" />
            </button>

            {/* Notifications Bell */}
            <button
              onClick={() => navigate('/dashboard/notifications')}
              className="relative p-2 rounded-lg hover:bg-green-50 transition-all duration-200 group"
            >
              <BellIcon className="w-5 h-5 text-gray-500 group-hover:text-green-600 transition-colors duration-200" />
              {/* Notification Badge - Only show if count > 0 */}
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 text-xs font-bold text-white rounded-full flex items-center justify-center"
                      style={{ backgroundColor: '#197560' }}>
                  {notificationCount > 9 ? '9+' : notificationCount}
                </span>
              )}
            </button>

            {/* User Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center space-x-2 px-3 py-1.5 rounded-lg hover:bg-green-50 hover:shadow-md transition-all duration-200 group"
              >
                <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: '#197560' }}>
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-green-700 transition-colors duration-200">
                    {user?.firstName || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 group-hover:text-green-600 transition-colors duration-200">
                    ID: {generateUserID()}
                  </p>
                </div>
                <ChevronDownIcon 
                  className={`h-4 w-4 text-gray-400 group-hover:text-green-600 transition-all duration-200 ${
                    isDropdownOpen ? 'rotate-180' : ''
                  }`} 
                />
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {user?.email}
                    </p>
                    <p className="text-xs text-green-600 font-medium mt-1">
                      ID: {generateUserID()}
                    </p>
                  </div>
                  
                  <button
                    onClick={() => {
                      setIsDropdownOpen(false);
                      navigate('/dashboard/account-settings');
                    }}
                    className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-all duration-200 group"
                  >
                    <Cog6ToothIcon className="h-5 w-5 mr-3 text-gray-400 group-hover:text-green-600 transition-colors duration-200" />
                    Account Settings
                  </button>
                  
                  <button
                    onClick={() => {
                      setIsDropdownOpen(false);
                      navigate('/dashboard/security');
                    }}
                    className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-all duration-200 group"
                  >
                    <ShieldCheckIcon className="h-5 w-5 mr-3 text-gray-400 group-hover:text-green-600 transition-colors duration-200" />
                    Security Center
                  </button>
                  
                  <button
                    onClick={() => {
                      setIsDropdownOpen(false);
                      logout();
                    }}
                    className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-all duration-200 group border-t border-gray-100"
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3 text-gray-400 group-hover:text-red-600 transition-colors duration-200" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Support Modal */}
      <SupportModal 
        isOpen={isSupportModalOpen} 
        onClose={() => setIsSupportModalOpen(false)} 
      />
    </header>
  );
};

export default Header;